/**
 * Entry screen, React edition of ui/Hud.ts's buildSplash.
 *
 * It exists for a reason beyond consent: the first macroscopic competency in
 * the curriculum is protocol, and entering a fab through gowning rather than
 * materialising on the floor is the first thing this environment can teach.
 * Staged rather than merely legible - the scrim clears toward the right so
 * the cleanroom behind it is the artwork - and worded for the hand at the
 * screen: teaching WASD to a student who has no W tells them the app was not
 * built for their device.
 *
 * Language and input-modality switches are just re-renders now; the old
 * hand-rolled replaceChildren rebuilds are gone.
 */

import { useEffect, useState } from 'react'
import { HALL_LAYOUT, hallIdFor, planIdOf, type HallLayout } from '../../fab/hall'
import { activePlan, getPlan } from '../../fab/floorPlan'
import { enterOtherFab } from './entrance'

/**
 * Both built-in fabs, and the custom floor last stood in, if any. Not every
 * plan on this machine: a library belongs to an account (fab/fabLibrary.ts),
 * and the entry screen is up before anyone has signed in.
 */
const halls = (): HallLayout[] => {
  const mine = activePlan()
  return ['bayChase', 'ballroom', ...(mine ? [hallIdFor(mine.id)] : [])]
}

const hallLabel = (h: HallLayout): string => {
  const planId = planIdOf(h)
  if (planId) return getPlan(planId)?.name ?? tOr('author.NewFloor', 'New floor')
  return h === 'ballroom' ? tOr('menu.HallBallroom', 'Fab B') : tOr('menu.HallBayChase', 'Fab A')
}
import { t, tOr } from '../../i18n/i18n'
import { walkEntered, setEntered } from '../input/walkLock'
import { useLang } from '../i18n/LanguageProvider'
import { LocaleHtml } from '../i18n/LocaleHtml'
import { useInputMode } from '../state/hooks'
import { useObservable } from '../state/useObservable'
import { BrandMark } from './BrandMark'
import { splashStore } from './splashStore'

export function Splash() {
  useLang()
  const touch = useInputMode()
  const wanted = useObservable(splashStore.subscribe, () => splashStore.wanted)
  const [leaving, setLeaving] = useState(false)

  const enter = (): void => {
    if (walkEntered()) return
    // The React clusters watch this flag; the floor's navigation appears.
    // Fade out rather than cut: the scene is already rendering behind, so the
    // student sees the room they were shown resolve into the room they are in.
    // Retiring through the store rather than a local "gone" flag is what lets
    // the screen come back later: "Back to entrance" shows it again, fresh.
    setEntered(true)
    setLeaving(true)
    window.setTimeout(() => {
      setLeaving(false)
      splashStore.hide()
    }, 520)
  }

  useEffect(() => {
    if (!wanted || leaving) return
    const onKey = (e: KeyboardEvent): void => {
      // Enter and Space also operate the language menu. A keypress aimed at a
      // form control belongs to that control, not to the door.
      const target = e.target as HTMLElement | null
      if (target && /^(SELECT|INPUT|TEXTAREA|BUTTON|OPTION)$/.test(target.tagName)) return
      if (e.code === 'Enter' || e.code === 'Space' || e.code === 'NumpadEnter') {
        e.preventDefault()
        enter()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, leaving])

  if (!wanted) return null

  const cap = (text: string) => <kbd>{text}</kbd>

  return (
    <div className={leaving ? 'splash leaving' : 'splash'} onClick={enter}>
      <div className="splash-inner">
        <div className="brand">
          <div className="mark">
            <BrandMark size={26} />
          </div>
          <div className="word">{t('name.SimFab')}</div>
        </div>

        <div className="eyebrow">{t('splash.Eyebrow')}</div>

        <LocaleHtml as="h1" k="splash.Headline" />

        <p className="sub">{t('splash.Sub')}</p>

        <ul className="keys">
          {touch ? (
            <>
              <li>
                {cap(t('splash.CapTap'))}
                <LocaleHtml k="splash.KeyWalkTouch" />
              </li>
              <li>
                {cap(t('splash.CapDrag'))}
                <LocaleHtml k="splash.KeyLookTouch" />
              </li>
              <li>
                {cap(t('splash.CapTap'))}
                <LocaleHtml k="splash.KeyOpenTouch" />
              </li>
              <li>
                {cap(t('splash.CapAsk'))}
                <LocaleHtml k="splash.KeyAsk" />
              </li>
            </>
          ) : (
            <>
              <li>
                {cap('W')}
                {cap('A')}
                {cap('S')}
                {cap('D')}
                <LocaleHtml k="splash.KeyWalk" />
              </li>
              <li>
                {cap(t('splash.CapLook'))}
                <LocaleHtml k="splash.KeyLook" />
              </li>
              <li>
                {cap(t('splash.CapOpen'))}
                <LocaleHtml k="splash.KeyOpen" />
              </li>
              <li>
                {cap(t('splash.CapAsk'))}
                <LocaleHtml k="splash.KeyAsk" />
              </li>
            </>
          )}
        </ul>

        <div className="cta-row">
          {/* One door per fab. This page's own fab is the primary button and
              what Enter, Space and a click anywhere do; the other fab is a
              reload away, so its button stops the click reaching the door
              and asks for the other hall instead. */}
          {halls().map((h) => {
            const here = h === HALL_LAYOUT
            return (
              <div
                key={h}
                className={here ? 'enter' : 'enter other'}
                role="button"
                onClick={
                  here
                    ? undefined
                    : (e) => {
                        e.stopPropagation()
                        enterOtherFab(h)
                      }
                }
              >
                <span>{t('splash.EnterFab', { fab: hallLabel(h) })}</span>
                <span className="arrow">→</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
